/**
 * API Service Layer
 * Handles all backend communication for the application
 */

const USER_SERVICE_URL = import.meta.env.VITE_USER_SERVICE_URL || 'http://localhost:5001';
const SCRAPING_SERVICE_URL = import.meta.env.VITE_SCRAPING_SERVICE_URL || 'http://localhost:5000';
const CRAWLER_SERVICE_URL = import.meta.env.VITE_CRAWLER_SERVICE_URL || 'http://localhost:5002';
const LOGGER_SERVICE_URL = import.meta.env.VITE_LOGGER_SERVICE_URL || 'http://localhost:5003';

// API Error class
export class APIError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'APIError';
    this.status = status;
  }
}

// Helper function for API calls
// Signal can be used with AbortController to cancel in-flight requests
// (e.g. on component unmount or user navigation)
export async function apiCall<T>(url: string, options: RequestInit = {}, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    ...options,
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
      const detail = error.detail;
      let message: string;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI validation errors: [{loc, msg, type}, ...]
        message = detail.map((e: { msg?: string; loc?: string[] }) =>
          e.loc ? `${e.loc.join('.')}: ${e.msg}` : (e.msg ?? JSON.stringify(e))
        ).join('; ');
      } else if (detail) {
        message = JSON.stringify(detail);
      } else {
        message = error.message ?? 'Request failed';
      }
      throw new APIError(response.status, message);
    }

    // 204 No Content — no body to parse
    if (response.status === 204) {
      return undefined as unknown as T;
    }

    return response.json();
}

// ============================================================================
// Authentication API
// ============================================================================

export interface LoginResponse {
  access_token: string;
  token_type: string;
  api_key: string;
  user: {
    id: number;
    username: string;
    email: string;
    role: 'admin' | 'user' | 'viewer';
    created_at: string;
  };
}

export interface RegisterResponse {
  access_token: string;
  token_type: string;
  api_key: string;
  user: {
    id: number;
    username: string;
    email: string;
    role: 'admin' | 'user' | 'viewer';
    created_at: string;
  };
}

export interface UserProfile {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'user' | 'viewer';
  created_at: string;
}

export const authAPI = {
  /**
   * Login user with username and password
   * Returns JWT token and API key
   */
  login: async (username: string, password: string): Promise<LoginResponse> => {
    return apiCall<LoginResponse>(`${USER_SERVICE_URL}/api/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  /**
   * Register new user with username, email, and password
   * Returns JWT token and API key
   */
  register: async (username: string, email: string, password: string): Promise<RegisterResponse> => {
    return apiCall<RegisterResponse>(`${USER_SERVICE_URL}/api/auth/register`, {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    });
  },

  /**
   * Get current user profile using API key
   */
  getProfile: async (apiKey: string): Promise<UserProfile> => {
    return apiCall<UserProfile>(`${USER_SERVICE_URL}/api/users/me`, {
      headers: {
        'X-API-Key': apiKey,
      },
    });
  },

  /**
   * Verify API key is valid
   */
  verifyToken: async (apiKey: string): Promise<boolean> => {
    try {
      await authAPI.getProfile(apiKey);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Request password reset (forgot password)
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${USER_SERVICE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Reset password using reset token
   */
  resetPassword: async (reset_token: string, new_password: string): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${USER_SERVICE_URL}/api/auth/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ reset_token, new_password }),
    });
  },

  /**
   * Change password (authenticated user)
   */
  changePassword: async (apiKey: string, current_password: string, new_password: string): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${USER_SERVICE_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'X-API-Key': apiKey,
      },
      body: JSON.stringify({ current_password, new_password }),
    });
  },

  /**
   * Update user profile (username and/or email)
   */
  updateProfile: async (apiKey: string, data: { username?: string; email?: string }): Promise<UserProfile> => {
    return apiCall<UserProfile>(`${USER_SERVICE_URL}/api/users/me`, {
      method: 'PUT',
      headers: {
        'X-API-Key': apiKey,
      },
      body: JSON.stringify(data),
    });
  },
};

// ============================================================================
// User Lookup API (for displaying creator information)
// ============================================================================

export interface UserInfo {
  id: number;
  username: string;
}

export interface UsersLookupResponse {
  users: Record<string, UserInfo>;
}

/**
 * Get users by their IDs for display purposes (e.g., showing creator name on projects)
 */
export const getUsersByIds = async (apiKey: string, userIds: number[]): Promise<UsersLookupResponse> => {
  if (userIds.length === 0) {
    return { users: {} };
  }
  const uniqueIds = [...new Set(userIds)];
  return apiCall<UsersLookupResponse>(`${USER_SERVICE_URL}/api/internal/users?user_ids=${uniqueIds.join(',')}`, {
    headers: { 'X-API-Key': apiKey },
  });
};

// ============================================================================
// Projects API
// ============================================================================

export interface Project {
  id: number;
  user_id: number;
  name: string;
  description: string;
  master_url: string | null;
  creator_username?: string;
  // FU-1: project-level corridor defaults (null = inherit ±20)
  price_corridor_min_pct?: number | null;
  price_corridor_max_pct?: number | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectCreate {
  name: string;
  description?: string;
  // FU-1: project-level corridor defaults (null = inherit ±20)
  price_corridor_min_pct?: number | null;
  price_corridor_max_pct?: number | null;
}

export interface ProjectUpdate {
  name?: string;
  description?: string;
  // FU-1: project-level corridor defaults (null = inherit ±20)
  price_corridor_min_pct?: number | null;
  price_corridor_max_pct?: number | null;
}

export const projectsAPI = {
  /**
   * Get all projects for current user
   */
  getAll: async (apiKey: string): Promise<Project[]> => {
    return apiCall<Project[]>(`${SCRAPING_SERVICE_URL}/api/projects`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Create new project
   */
  create: async (apiKey: string, data: ProjectCreate): Promise<Project> => {
    return apiCall<Project>(`${SCRAPING_SERVICE_URL}/api/projects`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get project by ID
   */
  getById: async (apiKey: string, projectId: number): Promise<Project> => {
    return apiCall<Project>(`${SCRAPING_SERVICE_URL}/api/projects/${projectId}`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Update project
   */
  update: async (apiKey: string, projectId: number, data: ProjectUpdate): Promise<Project> => {
    return apiCall<Project>(`${SCRAPING_SERVICE_URL}/api/projects/${projectId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete project
   */
  delete: async (apiKey: string, projectId: number): Promise<void> => {
    await apiCall<void>(`${SCRAPING_SERVICE_URL}/api/projects/${projectId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey },
    });
  },
};

// ============================================================================
// Tasks API
// ============================================================================

export interface Task {
  id: number;
  project_id: number;
  name: string;
  description: string;
  reference_urls: string[] | null;
  master_url: string | null;
  exclude_query_params: boolean;
  allow_pagination_params: boolean;
  url_exclusion_patterns: string[];
  has_graduated_prices: boolean;
  allow_pdfs: boolean;
  enable_variant_url_crawling: boolean;
  enable_platform_scraping: boolean;
  selected_platforms: number[];
  has_jobs: boolean;  // True if task has any jobs (locks structural fields)
  // Language & Localization
  task_language: string;
  language_mode: string;  // 'single' | 'hreflang' | 'directory'
  hreflang_languages: string[] | null;
  language_mappings: LanguageMappings | null;
  entity_refresh_policy?: Record<string, unknown> | null;
  uploaded_urls_count?: number;
  scope_entry_seeds?: Record<string, string[]> | null;
    upload_mode?: string;
    results_per_platform?: number;
    catalog_product_count?: number;
    skip_initial_analysis?: boolean;
    /** ID of the shop connector attached to this task, if any */
    connector_id?: string | null;
    /** Name of the shop connector attached to this task, if any */
    connector_name?: string | null;
    // Marketplace pricing workflow
    require_domestic_seller?: boolean;
    target_country_code?: string | null;
    price_corridor_min_pct?: number;
    price_corridor_max_pct?: number;
    // FU-2: per-task outlier thresholds (null = global ±70 default)
    outlier_threshold_min_pct?: number | null;
    outlier_threshold_max_pct?: number | null;
}

export interface LanguageMappings {
  directories?: Array<{ path: string; language: string }>;
  subdomains?: Array<{ subdomain: string; language: string }>;
  /**
   * Per-competitor language path overrides.
   * Key = hostname (e.g. "competitor.com").
   * If absent for a domain the BFS crawler applies no path filter — safe for
   * single-language sites; language is enforced at product normalization.
   * Add an entry to restrict a multilingual competitor to specific language
   * directories and keep crawl budget small.
   */
  per_domain?: Record<string, { directories?: Array<{ path: string; language: string }> }>;
}

export interface TaskCreate {
  name: string;
  description?: string;
  reference_urls?: string[];
  master_url?: string;
  exclude_query_params?: boolean;
  allow_pagination_params?: boolean;
  url_exclusion_patterns?: string[];
  allow_pdfs?: boolean;
  enable_platform_scraping?: boolean;
  selected_platforms?: number[];
  // Language & Localization
  task_language?: string;
  language_mode?: string;
  hreflang_languages?: string[] | null;
  language_mappings?: LanguageMappings | null;
  // Catalog Mode
  upload_mode?: string;
  results_per_platform?: number;
  // Crawl entry seeds
  scope_entry_seeds?: Record<string, string[]> | null;
  // Dynamic Refresh
  entity_refresh_policy?: Record<string, unknown> | null;
  // Staffelpreise / Graduated Prices
  has_graduated_prices?: boolean;
  // Variant crawling
  enable_variant_url_crawling?: boolean;
  // Marketplace pricing workflow
  require_domestic_seller?: boolean;
  target_country_code?: string | null;
  price_corridor_min_pct?: number;
  price_corridor_max_pct?: number;
  // FU-2: per-task outlier thresholds (null = global ±70 default)
  outlier_threshold_min_pct?: number | null;
  outlier_threshold_max_pct?: number | null;
}

export interface TaskUpdate {
  name?: string;
  description?: string;
  reference_urls?: string[];
  master_url?: string;
  exclude_query_params?: boolean;
  allow_pagination_params?: boolean;
  url_exclusion_patterns?: string[];
  allow_pdfs?: boolean;
  enable_platform_scraping?: boolean;
  selected_platforms?: number[];
  // Language & Localization
  task_language?: string;
  language_mode?: string;
  hreflang_languages?: string[] | null;
  language_mappings?: LanguageMappings | null;
  entity_refresh_policy?: Record<string, unknown> | null;
  // Catalog Mode
  upload_mode?: string;
  results_per_platform?: number;
  // Crawl entry seeds
  scope_entry_seeds?: Record<string, string[]> | null;
  // Marketplace pricing workflow
  require_domestic_seller?: boolean;
  target_country_code?: string | null;
  price_corridor_min_pct?: number;
  price_corridor_max_pct?: number;
  // FU-2: per-task outlier thresholds (null = global ±70 default)
  outlier_threshold_min_pct?: number | null;
  outlier_threshold_max_pct?: number | null;
}

export const tasksAPI = {
  /**
   * Get all tasks for a project
   */
  getAll: async (apiKey: string, projectId: number): Promise<Task[]> => {
    const tasks = await apiCall<Task[]>(`${SCRAPING_SERVICE_URL}/api/projects/${projectId}/tasks`, {
      headers: { 'X-API-Key': apiKey },
    });
    // Normalize reference_urls to always be an array
    return tasks.map(task => ({
      ...task,
      reference_urls: task.reference_urls || []
    }));
  },

  /**
   * Create new task
   */
  create: async (apiKey: string, projectId: number, data: TaskCreate): Promise<Task> => {
    const task = await apiCall<Task>(`${SCRAPING_SERVICE_URL}/api/projects/${projectId}/tasks`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
    return { ...task, reference_urls: task.reference_urls || [] };
  },

  /**
   * Get task by ID
   */
  getById: async (apiKey: string, taskId: number): Promise<Task> => {
    const task = await apiCall<Task>(`${SCRAPING_SERVICE_URL}/api/tasks/${taskId}`, {
      headers: { 'X-API-Key': apiKey },
    });
    return { ...task, reference_urls: task.reference_urls || [] };
  },

  /**
   * Update task
   */
  update: async (apiKey: string, taskId: number, data: TaskUpdate): Promise<Task> => {
    const task = await apiCall<Task>(`${SCRAPING_SERVICE_URL}/api/tasks/${taskId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
    return { ...task, reference_urls: task.reference_urls || [] };
  },

  /**
   * Delete task
   */
  delete: async (apiKey: string, taskId: number): Promise<void> => {
    await apiCall<void>(`${SCRAPING_SERVICE_URL}/api/tasks/${taskId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey },
    });
  },
};

// ============================================================================
// Jobs API
// ============================================================================

export interface DiscoveryConfig {
  keywords?: string[];
  threshold?: number;
  content_types?: string[];
  url_patterns?: string[];
  file_extensions?: string[];
  page_types?: string[];
}

// Crawl configuration for discovery jobs
export interface CrawlConfig {
  max_pages?: number;
  crawl_whole_domain?: boolean;
  enable_js_rendering?: boolean;
  csv_urls_are_limit?: boolean;
  competitor_batch_size?: number;
  max_empty_batches?: number;
  enable_pre_filter?: boolean;  // undefined = auto-detect, true/false = explicit override
}

// Manual mode pipeline step defaults
export interface PipelineConfig {
  skip_crawl?: boolean;
  skip_extract?: boolean;
  skip_normalization?: boolean;
  skip_matching?: boolean;
  skip_platforms?: boolean;
  auto_normalize?: boolean;  // true = AI, false = selector
}

export interface Job {
  id: number;
  task_id: number;
  name: string;
  job_type: string;
  execution_mode: 'extraction' | 'discovery';
  auto_execute?: boolean;  // True = automated full pipeline, False = manual step-by-step
  skip_initial_analysis?: boolean;  // True = skip AI selector discovery, use manual selectors
  pipeline_config?: PipelineConfig | null;  // Manual mode pipeline step defaults
  discovery_config: DiscoveryConfig | null;
  crawl_config?: CrawlConfig | null;  // Crawl settings for discovery jobs
  reference_urls: string[] | null;
  // Modular extraction config
  enabled_secondary_modules?: string[];  // e.g., ['company_info', 'best_sellers']
  module_results?: Record<string, ModuleResult> | null;  // Results from each module
  // Status fields
  status: 'pending' | 'running' | 'completed' | 'failed';
  celery_task_id: string | null;
  selectors_discovered: number;
  reliability_score: number;
  error_message: string | null;
  // Execution statistics
  last_crawl_id?: number | null;
  urls_crawled?: number;
  urls_discovered?: number;
  products_extracted?: number;
  products_normalized?: number;
  products_matched?: number;
  last_execution_status?: 'success' | 'partial' | 'failed' | 'running' | null;
  last_execution_message?: string | null;
  last_execution_phase?: string | null;
  // Platform scraping status
  platform_scraping_status?: 'running' | 'completed' | 'failed' | 'normalizing' | null;
  // AI normalization mode for master products: 'auto' | 'quick' | 'batch'
  master_normalization_mode?: string;
  // FU-3 — opt-in completion email for manual runs
  notify_on_completion?: boolean;
  // Timestamps
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

// Modular extraction types
export interface ModuleResult {
  status: 'pending' | 'completed' | 'failed';
  pages_analyzed: number;
  source_urls: string[];
  data: Record<string, unknown>;
  error?: string;
}

export interface ExtractionModule {
  id: string;
  name: string;
  description: string;
  category: 'primary' | 'secondary';
  can_be_primary: boolean;
  max_pages?: number;
}

export interface ExtractionModulesResponse {
  primary_modules: Record<string, ExtractionModule>;
  secondary_modules: Record<string, ExtractionModule>;
  all_modules: Record<string, ExtractionModule>;
}

export interface JobCreate {
  name: string;
  job_type: string;
  execution_mode?: 'extraction' | 'discovery';
  auto_execute?: boolean;  // True = automated full pipeline, False = manual step-by-step
  skip_initial_analysis?: boolean;  // True = skip AI selector discovery, use manual selectors
  pipeline_config?: PipelineConfig | null;  // Manual mode pipeline step defaults
  discovery_config?: DiscoveryConfig;
  crawl_config?: CrawlConfig;  // Crawl settings for discovery jobs
  reference_urls?: string[];
  enabled_secondary_modules?: string[];  // Secondary modules to enable
  // AI normalization mode for master products: 'auto' | 'quick' | 'batch'
  master_normalization_mode?: string;
  // FU-3 — opt-in completion email for manual runs
  notify_on_completion?: boolean;
}

export interface JobUpdate {
  name?: string;
  reference_urls?: string[];
  crawl_config?: CrawlConfig;
  // AI normalization mode for master products: 'auto' | 'quick' | 'batch'
  master_normalization_mode?: string;
  // FU-3 — opt-in completion email for manual runs
  notify_on_completion?: boolean;
}

export interface JobExecuteRequest {
  url?: string;
  chain_pipeline?: Partial<ExecuteFullPipelineRequest>;
}

export interface JobExecutionResponse {
  job_id: number;
  celery_task_id: string;
  status: string;
  message: string;
  processing: string;
  estimated_time: string;
}

export interface ExecuteFullPipelineRequest {
  job_id: number;
  task_id: number;
  project_id: number;
  user_id: number;
  force_new_crawl?: boolean;
  enable_normalization?: boolean;
  master_domain?: string;  // If provided, matching will be auto-enabled
  max_pages?: number;  // Ignored if crawl_whole_domain=True
  crawl_whole_domain?: boolean;  // If True, crawl all pages
  crawl_whole_domain_domains?: string[];  // Per-domain: skip batch limit for these hostnames
  enable_js_rendering?: boolean;  // Enable Selenium for JS-heavy sites
  enable_pagination?: boolean;  // Follow pagination links
  csv_urls_are_limit?: boolean;  // If True, crawl only uploaded CSV URLs
  has_graduated_prices?: boolean;  // If True, expect multiple quantity-based prices
  refresh_mode?: boolean;  // If True, reuse URLs from latest completed execution
  refresh_run_mode?: 'force_all' | 'due_only' | 'custom_selection';
  custom_entity_keys?: string[];
  competitor_batch_size?: number;  // URLs per competitor batch (default: 200)
  max_empty_batches?: number;  // Stop after N consecutive empty batches (default: 3, 0=disabled)
  enable_pre_filter?: boolean;  // undefined = auto-detect, true/false = explicit override
  rediscover_urls?: boolean;  // If True, re-crawl to discover new URLs during refresh
  renormalize_only?: boolean;  // If True, skip crawl/extract, only re-normalize + match
  refresh_platforms_only?: boolean;  // If True, only refresh platform scraping
  resume_from?: string;  // Phase to resume from (for resume action)
  override_skip_crawl?: boolean;
  override_skip_extract?: boolean;
  override_skip_normalize?: boolean;
  override_skip_matching?: boolean;
  override_skip_platforms?: boolean;
  /** Pre-pipeline connector sync: pull master data from connector before execution */
  connector_sync?: boolean;
}

export interface PipelineStep {
  step: string;
  status: string;
  started_at?: string;
  completed_at?: string;
  error?: string;
}

export interface WorkflowStatusResponse {
  job_id: number;
  task_id: number;
  workflow_status?: string;
  status?: string;  // 'pipeline_running', 'already_complete', etc.
  message?: string;
  resume_from?: string;  // 'crawl', 'extract', 'normalize', 'platform_scraping', 'complete'
  pipeline_state?: {
    has_crawl: boolean;
    crawl_execution_id?: number;
    has_extractions: boolean;
    extraction_count: number;
    has_normalization: boolean;
    normalization_job_id?: number;
    products_normalized: number;
    platform_scraping_status?: string;
    needs_platform_retry: boolean;
    resume_from: string;
  };
  steps: PipelineStep[];
  crawl_celery_task_id?: string;
  extract_celery_task_id?: string;
  normalize_celery_task_id?: string;
  started_at?: string;
  pipeline_started?: string;
}

export interface JobStatusResponse {
  job_id: number;
  status: string;
  selectors_discovered: number;
  reliability_score: number;
  celery_status?: string;
  progress?: number;
}

// Pipeline status from crawler-service (tracks actual running state)
export interface PipelineStageProgress {
  stage: string;
  current: number;
  total: number;
  message: string;
}

export type PipelineStage =
  | 'idle'
  | 'discovering'
  | 'crawling'
  | 'extracting'
  | 'normalizing'
  | 'pre_filtering'
  | 'normalizing_competitors'
  | 'matching'
  | 'platform_scraping'
  | 'field_mapping'
  | 'completed'
  | 'done'
  | 'failed'
  | 'cancelled';

export interface PipelineStatusResponse {
  success: boolean;
  job_id: number;
  task_id: number | null;
  is_running: boolean;  // Overall pipeline running status (includes selector discovery)
  current_stage: PipelineStage;  // NEW: Current stage identifier
  stage_progress: PipelineStageProgress | null;  // NEW: Detailed progress
  attribute_schema_status?: string;  // Schema analysis status
  // Selector discovery status (extraction mode)
  selector_discovery?: {
    is_running: boolean;
    has_selectors: boolean;
    selectors_count: number;
    progress: PipelineStageProgress | null;
  };
  crawl: {
    id: number;
    status: string;
    urls_crawled: number;
    urls_discovered: number;
    is_running: boolean;
  } | null;
  extraction: {
    count: number;
    completed: boolean;
  };
  normalization: {
    exists: boolean;
    status: string | null;
    products_normalized: number;
  };
  matching: {
    enabled: boolean;
    count: number;
    products_matched: number;
  };
  platform_scraping?: {
    products_extracted: number;
    is_running: boolean;
  };
}

export interface JobType {
  id: string;
  name: string;
  description: string;
  execution_mode: 'extraction' | 'discovery';
  requires_selectors: boolean;
  requires_ai_analysis: boolean;
  supported_filters?: string[];
  default_keywords?: string[];
  default_content_types?: string[];
}

export interface Selector {
  id: number;
  job_id: number;
  category: string;
  name: string;
  url_source: string;
  css_selector: string | null;
  xpath_selector: string | null;
  selector_type: string | null;
  extraction_method: string | null;
  reliability: string;
  regex_pattern: string | null;
  requires_js?: boolean;
  config: object | null;
  sample_data: unknown;
  created_at: string;
}

export interface SelectorsGroupedResponse {
  job_id: number;
  job_type: string;
  status: string;
  selectors_count: number;
  reliability_score: number;
  selectors: {
    [category: string]: {
      [name: string]: Selector;
    };
  };
  created_at: string;
}

export interface SelectorUpdate {
  name?: string;
  category?: string;
  css_selector?: string;
  xpath_selector?: string;
  selector_type?: string;
  extraction_method?: string;
  reliability?: string;
  regex_pattern?: string;
  url_source?: string;
  requires_js?: boolean;
}

// ============================================================================
// Pipeline Execution Types (Unified Status Tracking)
// ============================================================================

export type ExecutionType = 'crawl' | 'extraction' | 'full_pipeline' | 'discovery_crawl';
export type ExecutionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
export type ExecutionPhase =
  | 'crawling'
  | 'extracting'
  | 'normalizing'
  | 'pre_filtering'
  | 'normalizing_competitors'
  | 'matching'
  | 'platform_scraping'
  | 'field_mapping'
  | 'done';

export interface PipelineExecution {
  id: number;
  job_id: number;
  task_id: number;
  execution_type: ExecutionType;
  status: ExecutionStatus;
  current_phase: ExecutionPhase | null;
  celery_task_id: string | null;
  crawler_execution_id: number | null;
  progress: {
    urls_crawled?: number;
    urls_total?: number;
    products_extracted?: number;
    products_normalized?: number;
    products_matched?: number;
    current_step?: string;
    [key: string]: unknown;
  };
  error_message: string | null;
  error_details: Record<string, unknown> | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  started_by: number | null;
}

export interface PipelineExecutionCreate {
  job_id: number;
  task_id: number;
  execution_type: ExecutionType;
  started_by?: number;
}

export interface PipelineExecutionUpdate {
  status?: ExecutionStatus;
  current_phase?: ExecutionPhase;
  celery_task_id?: string;
  crawler_execution_id?: number;
  progress?: Record<string, unknown>;
  error_message?: string;
  error_details?: Record<string, unknown>;
}

// Pipeline Execution API
export const pipelineExecutionsAPI = {
  /**
   * Create a new pipeline execution
   */
  create: async (apiKey: string, data: PipelineExecutionCreate): Promise<PipelineExecution> => {
    return apiCall<PipelineExecution>(`${SCRAPING_SERVICE_URL}/api/pipeline-executions`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get the latest execution for a job
   */
  getLatest: async (apiKey: string, jobId: number): Promise<PipelineExecution | null> => {
    return apiCall<PipelineExecution | null>(`${SCRAPING_SERVICE_URL}/api/pipeline-executions/job/${jobId}/latest`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get all active executions for a task
   */
  getActiveForTask: async (apiKey: string, taskId: number): Promise<PipelineExecution[]> => {
    return apiCall<PipelineExecution[]>(`${SCRAPING_SERVICE_URL}/api/pipeline-executions/task/${taskId}/active`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get all active executions
   */
  getAllActive: async (apiKey: string): Promise<PipelineExecution[]> => {
    return apiCall<PipelineExecution[]>(`${SCRAPING_SERVICE_URL}/api/pipeline-executions/active`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Update execution status (typically called by backend)
   */
  updateStatus: async (apiKey: string, executionId: number, data: PipelineExecutionUpdate): Promise<PipelineExecution> => {
    return apiCall<PipelineExecution>(`${SCRAPING_SERVICE_URL}/api/pipeline-executions/${executionId}/status`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get a specific execution
   */
  get: async (apiKey: string, executionId: number): Promise<PipelineExecution> => {
    return apiCall<PipelineExecution>(`${SCRAPING_SERVICE_URL}/api/pipeline-executions/${executionId}`, {
      headers: { 'X-API-Key': apiKey },
    });
  },
};

export const jobsAPI = {
  /**
   * Get all jobs for a task
   */
  getAll: async (apiKey: string, taskId: number): Promise<Job[]> => {
    const res = await apiCall<{ items: Job[]; total: number; page: number; per_page: number } | Job[]>(
      `${SCRAPING_SERVICE_URL}/api/tasks/${taskId}/jobs?per_page=200`,
      { headers: { 'X-API-Key': apiKey } },
    );
    // Unwrap paginated response — backend returns {items: [...], total, page, per_page}
    return Array.isArray(res) ? res : res.items;
  },

  /**
   * Create new job
   */
  create: async (apiKey: string, taskId: number, data: JobCreate): Promise<Job> => {
    return apiCall<Job>(`${SCRAPING_SERVICE_URL}/api/tasks/${taskId}/jobs`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get job by ID
   */
  getById: async (apiKey: string, jobId: number): Promise<Job> => {
    return apiCall<Job>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Update job
   */
  update: async (apiKey: string, jobId: number, data: JobUpdate): Promise<Job> => {
    return apiCall<Job>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete job
   */
  delete: async (apiKey: string, jobId: number): Promise<void> => {
    await apiCall<void>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Execute job
   */
  execute: async (apiKey: string, jobId: number, data?: JobExecuteRequest): Promise<JobExecutionResponse> => {
    return apiCall<JobExecutionResponse>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/execute`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data || {}),
    });
  },

  /**
   * Get job status
   */
  getStatus: async (apiKey: string, jobId: number): Promise<JobStatusResponse> => {
    return apiCall<JobStatusResponse>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/status`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get pipeline status from crawler service (extraction, normalization, matching)
   * This returns the actual running state from the database
   */
  getPipelineStatus: async (apiKey: string, jobId: number): Promise<PipelineStatusResponse> => {
    return apiCall<PipelineStatusResponse>(`${CRAWLER_SERVICE_URL}/api/status/job/${jobId}`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get available job types
   */
  getJobTypes: async (apiKey: string): Promise<JobType[]> => {
    const response = await apiCall<Record<string, unknown>>(`${SCRAPING_SERVICE_URL}/api/job-types`, {
      headers: { 'X-API-Key': apiKey },
    });
    return Object.entries(response).map(([id, config]) => {
      const cfg = (typeof config === 'object' && config !== null ? config : {}) as Record<string, unknown>;
      return {
        id,
        name: cfg.name as string,
        description: cfg.description as string,
        execution_mode: (cfg.execution_mode || 'extraction') as JobType['execution_mode'],
        requires_selectors: (cfg.requires_selectors ?? false) as boolean,
        requires_ai_analysis: (cfg.requires_ai_analysis ?? false) as boolean,
        supported_filters: cfg.supported_filters as string[] | undefined,
        default_keywords: cfg.default_keywords as string[] | undefined,
        default_content_types: cfg.default_content_types as string[] | undefined,
      };
    });
  },

  /**
   * Get available extraction modules (primary and secondary)
   * Secondary modules can be enabled during job creation to run during crawl
   */
  getExtractionModules: async (apiKey: string): Promise<ExtractionModulesResponse> => {
    return apiCall<ExtractionModulesResponse>(`${SCRAPING_SERVICE_URL}/api/extraction-modules`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get discovered selectors for a job
   */
  getSelectors: async (apiKey: string, jobId: number): Promise<SelectorsGroupedResponse> => {
    return apiCall<SelectorsGroupedResponse>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/selectors`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Update a selector for a job
   */
  updateSelector: async (apiKey: string, jobId: number, selectorId: number, data: SelectorUpdate): Promise<Selector> => {
    return apiCall<Selector>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/selectors/${selectorId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
  },

  /**
   * Create a new selector for a job
   */
  createSelector: async (apiKey: string, jobId: number, data: SelectorUpdate): Promise<Selector> => {
    return apiCall<Selector>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/selectors`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a selector from a job
   */
  deleteSelector: async (apiKey: string, jobId: number, selectorId: number): Promise<void> => {
    return apiCall<void>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/selectors/${selectorId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Stop a running pipeline for a job
   * @param stage - Optional: 'crawl', 'extraction', 'normalization' to stop specific stage
   */
  stopPipeline: async (
    apiKey: string,
    jobId: number,
    stage?: 'crawl' | 'extraction' | 'normalization'
  ): Promise<{ success: boolean; message: string; stopped_components: string[]; stage_requested?: string }> => {
    return apiCall<{ success: boolean; message: string; stopped_components: string[]; stage_requested?: string }>(
      `${CRAWLER_SERVICE_URL}/api/pipeline/job/${jobId}/stop`,
      {
        method: 'POST',
        headers: { 'X-API-Key': apiKey },
        body: stage ? JSON.stringify({ stage }) : undefined,
      }
    );
  },

  /**
   * Get execution URLs from the last completed crawl for a job
   */
  getExecutionUrls: async (
    apiKey: string,
    jobId: number,
    taskId: number
  ): Promise<{ shop_urls: string[]; platform_urls: Record<string, string[]>; crawl_execution_id: number | null; platform_crawl_execution_id: number | null }> => {
    return apiCall(`${CRAWLER_SERVICE_URL}/api/pipeline/job/${jobId}/execution-urls?task_id=${taskId}`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  // ==========================================================================
  // Individual Stage Triggers (for Semi-Automatic Pipeline)
  // ==========================================================================

  /**
   * Start crawl only for a task
   */
  startCrawl: async (
    apiKey: string,
    request: {
      task_id: number;
      project_id: number;
      user_id: number;
      job_id?: number;  // Optional: associate crawl with a specific job for status tracking
      force_recrawl?: boolean;
      max_pages?: number;
      enable_js_rendering?: boolean;
      enable_pagination?: boolean;
      has_csv_urls?: boolean;
      csv_urls_are_limit?: boolean;
      urls?: string[];
    }
  ): Promise<{
    success: boolean;
    message: string;
    crawl_execution_id?: number;
    pipeline_execution_id?: number;  // Unified status tracking
    celery_task_id?: string;
    reusing_existing?: boolean;
  }> => {
    return apiCall(`${CRAWLER_SERVICE_URL}/api/crawl/task`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(request),
    });
  },

  /**
   * Start extraction only for a job
   */
  startExtraction: async (
    apiKey: string,
    request: {
      job_id: number;
      task_id: number;
      project_id: number;
      user_id: number;
      crawl_execution_id?: number;
      force_new_crawl?: boolean;
    }
  ): Promise<{
    success: boolean;
    message: string;
    extraction_count?: number;
  }> => {
    return apiCall(`${CRAWLER_SERVICE_URL}/api/extract/job`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(request),
    });
  },

  /**
   * Start normalization only for a job
   */
  startNormalization: async (
    apiKey: string,
    request: {
      job_id: number;
      task_id: number;
      project_id: number;
      job_type: string;
      enable_matching?: boolean;
      master_domain?: string;
      has_graduated_prices?: boolean;
    }
  ): Promise<{
    success: boolean;
    message: string;
    normalization_job?: {
      id: number;
      status: string;
    };
    celery_task_id?: string;
  }> => {
    return apiCall(`${CRAWLER_SERVICE_URL}/api/normalize/job`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(request),
    });
  },

  /**
   * Re-run matching for a completed normalization job
   */
  runMatching: async (
    apiKey: string,
    jobId: number,
    masterDomain?: string
  ): Promise<{
    success: boolean;
    message: string;
    matches_found?: number;
  }> => {
    return apiCall(`${CRAWLER_SERVICE_URL}/api/normalize/job/${jobId}/match`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: masterDomain ? JSON.stringify({ master_domain: masterDomain }) : undefined,
    });
  },

  /**
   * Get normalization status and results for a job
   */
  getNormalizationResults: async (
    apiKey: string,
    jobId: number
  ): Promise<{
    success: boolean;
    normalization_job: {
      id: number;
      status: string;
      products_normalized: number;
      products_matched: number;
    };
    products_count: number;
    products: Array<{
      id: number;
      normalized_data: Record<string, unknown>;
    }>;
  }> => {
    return apiCall(`${CRAWLER_SERVICE_URL}/api/normalize/job/${jobId}`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get matches for a job
   */
  getMatches: async (
    apiKey: string,
    jobId: number
  ): Promise<{
    success: boolean;
    matches: Array<{
      id: number;
      master_product_id: number;
      matched_product_id: number;
      match_score: number;
    }>;
  }> => {
    return apiCall(`${CRAWLER_SERVICE_URL}/api/normalize/job/${jobId}/matches`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Execute full pipeline (crawl + extract + normalize) after selector discovery
   */
  executeFullPipeline: async (
    apiKey: string,
    request: ExecuteFullPipelineRequest
  ): Promise<WorkflowStatusResponse> => {
    return apiCall<WorkflowStatusResponse>(`${CRAWLER_SERVICE_URL}/api/execute/full-pipeline`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(request),
    });
  },

  /**
   * Analyze attributes to generate schema for normalization
   * Called after extraction completes and before normalization
   */
  analyzeAttributes: async (
    apiKey: string,
    jobId: number,
    userId: number
  ): Promise<{
    success: boolean;
    schema: Record<string, unknown>;
    message: string;
  }> => {
    return apiCall(`${CRAWLER_SERVICE_URL}/api/jobs/${jobId}/analyze-attributes`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({ user_id: userId }),
    });
  },

  /**
   * Run normalization directly (without showing crawl config)
   * Used when extraction and attribute analysis are already complete
   */
  runNormalization: async (
    apiKey: string,
    jobId: number,
    taskId: number,
    projectId: number,
    userId: number,
    enableMatching: boolean = true,
    masterDomain?: string,
    jobType: string = 'analyze_products'
  ): Promise<{
    success: boolean;
    message: string;
    normalization_job?: {
      id: number;
      status: string;
    };
    celery_task_id?: string;
  }> => {
    return apiCall(`${CRAWLER_SERVICE_URL}/api/normalize/job`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({
        job_id: jobId,
        task_id: taskId,
        project_id: projectId,
        user_id: userId,
        job_type: jobType,
        enable_matching: enableMatching,
        master_domain: masterDomain,
      }),
    });
  },
};

// ============================================================================
// Job Cookie Sets API
// ============================================================================

export type CookieSetStatus = 'active' | 'suspected' | 'expired' | 'disabled';

export interface CookieEntry {
  name: string;
  value: string;
  domain?: string | null;
  path?: string | null;
  secure?: boolean;
  http_only?: boolean;
}

export interface CookieSet {
  id: number;
  job_id: number;
  domain: string;
  label: string;
  status: CookieSetStatus;
  cookie_count: number;
  header_count: number;
  last_used_at: string | null;
  last_suspect_at: string | null;
  suspect_count: number;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CookieSetDetail extends CookieSet {
  cookie_entries: CookieEntry[];
  extra_header_keys: string[];
}

export interface CookieSetCreate {
  domain: string;
  label: string;
  cookie_entries: CookieEntry[];
  extra_headers?: Record<string, string>;
  notes?: string;
}

export interface CookieSetUpdate {
  label?: string;
  cookie_entries?: CookieEntry[];
  extra_headers?: Record<string, string>;
  status?: CookieSetStatus;
  notes?: string;
}

export const cookieSetsAPI = {
  getAll: (apiKey: string, jobId: number): Promise<CookieSet[]> => {
    return apiCall<CookieSet[]>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/cookie-sets`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  getById: (apiKey: string, jobId: number, cookieSetId: number): Promise<CookieSetDetail> => {
    return apiCall<CookieSetDetail>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/cookie-sets/${cookieSetId}`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  create: (apiKey: string, jobId: number, payload: CookieSetCreate): Promise<CookieSet> => {
    return apiCall<CookieSet>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/cookie-sets`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(payload),
    });
  },

  update: (apiKey: string, jobId: number, cookieSetId: number, payload: CookieSetUpdate): Promise<CookieSet> => {
    return apiCall<CookieSet>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/cookie-sets/${cookieSetId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(payload),
    });
  },

  remove: (apiKey: string, jobId: number, cookieSetId: number): Promise<{ deleted: boolean }> => {
    return apiCall<{ deleted: boolean }>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/cookie-sets/${cookieSetId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey },
    });
  },

  markActive: (apiKey: string, jobId: number, cookieSetId: number): Promise<CookieSet> => {
    return apiCall<CookieSet>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/cookie-sets/${cookieSetId}/mark-active`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
    });
  },

  markExpired: (apiKey: string, jobId: number, cookieSetId: number): Promise<CookieSet> => {
    return apiCall<CookieSet>(`${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/cookie-sets/${cookieSetId}/mark-expired`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
    });
  },
};

// ============================================================================
// Uploaded URLs API (CSV Upload)
// ============================================================================

export interface UploadedUrl {
  id: number;
  url: string;
  entity_type: 'domain' | 'master' | 'competitor' | 'platform';
  entity_key: string | null;
  label: string | null;
  language: string | null;
  row_number: number;
  status: 'pending' | 'crawled' | 'failed' | 'skipped';
  error_detail: string | null;
  last_crawled_at: string | null;
  created_at: string;
}

export interface UploadedUrlStats {
  total: number;
  pending?: number;
  crawled?: number;
  failed?: number;
  skipped?: number;
  by_status: {
    pending: number;
    crawled: number;
    failed: number;
    skipped: number;
  };
}

export interface UploadedUrlScope {
  entityType?: 'domain' | 'master' | 'competitor' | 'platform';
  entityKey?: string;
}

export interface CsvUploadResult {
  message: string;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  total_rows: number;
  invalid_urls: Array<{ row: number; value: string; reason: string }>;
  language_warnings: Array<{ url: string; tld: string; expected_language: string; declared_language: string }>;
}

export interface UploadedUrlsResponse {
  urls: UploadedUrl[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export const uploadedUrlsAPI = {
  upload: (apiKey: string, taskId: number, file: File, scope?: UploadedUrlScope): Promise<CsvUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    if (scope?.entityType) params.append('entity_type', scope.entityType);
    if (scope?.entityKey) params.append('entity_key', scope.entityKey);
    const query = params.toString();
    // For FormData, do NOT set Content-Type — browser sets multipart boundary automatically
    return fetch(`${SCRAPING_SERVICE_URL}/api/tasks/${taskId}/upload-urls${query ? `?${query}` : ''}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: formData,
    }).then(async (res) => {
      if (!res.ok) {
        const error = await res.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(error.detail || `Upload failed: ${res.status}`);
      }
      return res.json();
    });
  },

  getAll: (apiKey: string, taskId: number, page = 1, perPage = 50, status?: string, scope?: UploadedUrlScope): Promise<UploadedUrlsResponse> => {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (status) params.append('status', status);
    if (scope?.entityType) params.append('entity_type', scope.entityType);
    if (scope?.entityKey) params.append('entity_key', scope.entityKey);
    return apiCall<UploadedUrlsResponse>(
      `${SCRAPING_SERVICE_URL}/api/tasks/${taskId}/uploaded-urls?${params}`,
      { headers: { 'X-API-Key': apiKey } }
    );
  },

  clear: (apiKey: string, taskId: number, scope?: UploadedUrlScope): Promise<{ message: string; deleted_count: number }> => {
    const params = new URLSearchParams();
    if (scope?.entityType) params.append('entity_type', scope.entityType);
    if (scope?.entityKey) params.append('entity_key', scope.entityKey);
    const query = params.toString();
    return apiCall<{ message: string; deleted_count: number }>(
      `${SCRAPING_SERVICE_URL}/api/tasks/${taskId}/uploaded-urls${query ? `?${query}` : ''}`,
      { method: 'DELETE', headers: { 'X-API-Key': apiKey } }
    );
  },

  getStats: (apiKey: string, taskId: number, scope?: UploadedUrlScope): Promise<UploadedUrlStats> => {
    const params = new URLSearchParams();
    if (scope?.entityType) params.append('entity_type', scope.entityType);
    if (scope?.entityKey) params.append('entity_key', scope.entityKey);
    const query = params.toString();
    return apiCall<UploadedUrlStats>(
      `${SCRAPING_SERVICE_URL}/api/tasks/${taskId}/uploaded-urls/stats${query ? `?${query}` : ''}`,
      { headers: { 'X-API-Key': apiKey } }
    );
  },

  updateUrl: (apiKey: string, taskId: number, urlId: number, data: { label?: string; language?: string }): Promise<UploadedUrl> => {
    return apiCall<UploadedUrl>(
      `${SCRAPING_SERVICE_URL}/api/tasks/${taskId}/uploaded-urls/${urlId}`,
      {
        method: 'PATCH',
        headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
  },
};

// ============================================================================
// Catalog Upload API
// ============================================================================

export interface CatalogUploadResult {
  success: boolean;
  task_id: number;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  missing_ean_count: number;
  total_rows: number;
  has_header: boolean;
  detected_headers: string[];
  invalid_rows: Array<{ row: number; value: string; reason: string }>;
  preview: Array<Record<string, unknown>>;
}

export interface CatalogProduct {
  id: number;
  task_id: number;
  product_name: string;
  ean: string | null;
  reference_price: number | null;
  external_id: string | null;
  variant_id: string | null;
  currency: string;
  status: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface CatalogProductsResponse {
  task_id: number;
  total: number;
  page: number;
  per_page: number;
  products: CatalogProduct[];
}

export const catalogAPI = {
  upload: (apiKey: string, taskId: number, file: File): Promise<CatalogUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return fetch(`${SCRAPING_SERVICE_URL}/api/tasks/${taskId}/upload-catalog`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: formData,
    }).then(async (res) => {
      if (!res.ok) {
        const error = await res.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(error.detail || `Upload failed: ${res.status}`);
      }
      return res.json();
    });
  },

  getAll: (apiKey: string, taskId: number, page = 1, perPage = 50): Promise<CatalogProductsResponse> => {
    return fetch(`${SCRAPING_SERVICE_URL}/api/tasks/${taskId}/catalog-products?page=${page}&per_page=${perPage}`, {
      headers: { 'X-API-Key': apiKey },
    }).then(async (res) => {
      if (!res.ok) {
        const error = await res.json().catch(() => ({ detail: 'Failed to fetch catalog products' }));
        throw new Error(error.detail || `Fetch failed: ${res.status}`);
      }
      return res.json();
    });
  },
};

// ============================================================================
// Discovery Results API
// ============================================================================

export interface DiscoveryResult {
  url: string;
  title: string;
  content_type: string;
  relevance_score: number;
  matched_keywords: string[];
  snippet?: string;
  discovered_at: string;
}

export interface DiscoveryResultsResponse {
  job_id: number;
  total_results: number;
  results: DiscoveryResult[];
}

export interface DiscoveryStatsResponse {
  job_id: number;
  total_matches: number;
  content_types: Array<{
    type: string;
    count: number;
    avg_score: number;
  }>;
  top_keywords: Array<{
    keyword: string;
    count: number;
  }>;
  top_results: DiscoveryResult[];
}

export const discoveryAPI = {
  /**
   * Get discovery results for a job
   */
  getResults: async (
    apiKey: string,
    jobId: number,
    options?: {
      content_type?: string;
      min_score?: number;
      limit?: number;
    }
  ): Promise<DiscoveryResultsResponse> => {
    const params = new URLSearchParams({
      job_id: jobId.toString(),
      ...(options?.content_type && { content_type: options.content_type }),
      ...(options?.min_score !== undefined && { min_score: options.min_score.toString() }),
      ...(options?.limit && { limit: options.limit.toString() }),
    });

    return apiCall<DiscoveryResultsResponse>(
      `${SCRAPING_SERVICE_URL}/api/discover/results/${jobId}?${params}`,
      {
        headers: { 'X-API-Key': apiKey },
      }
    );
  },

  /**
   * Get statistics for a discovery job
   */
  getStats: async (apiKey: string, jobId: number): Promise<DiscoveryStatsResponse> => {
    return apiCall<DiscoveryStatsResponse>(
      `${SCRAPING_SERVICE_URL}/api/discover/stats/${jobId}`,
      {
        headers: { 'X-API-Key': apiKey },
      }
    );
  },
};

// ============================================================================
// Health Check API
// ============================================================================

export interface HealthStatus {
  status: string;
  service: string;
  database?: string;
}

export const healthAPI = {
  checkUserService: async (): Promise<HealthStatus> => {
    return apiCall<HealthStatus>(`${USER_SERVICE_URL}/api/health`);
  },

  checkScrapingService: async (): Promise<HealthStatus> => {
    return apiCall<HealthStatus>(`${SCRAPING_SERVICE_URL}/api/health`);
  },

  checkCrawlerService: async (): Promise<HealthStatus> => {
    return apiCall<HealthStatus>(`${CRAWLER_SERVICE_URL}/api/health`);
  },

  checkLoggerService: async (): Promise<HealthStatus> => {
    return apiCall<HealthStatus>(`${LOGGER_SERVICE_URL}/api/health`);
  },

  checkAll: async (): Promise<Record<string, HealthStatus>> => {
    const [user, scraping, crawler, logger] = await Promise.allSettled([
      healthAPI.checkUserService(),
      healthAPI.checkScrapingService(),
      healthAPI.checkCrawlerService(),
      healthAPI.checkLoggerService(),
    ]);

    return {
      'user-service': user.status === 'fulfilled' ? user.value : { status: 'error', service: 'user-service' },
      'scraping-service': scraping.status === 'fulfilled' ? scraping.value : { status: 'error', service: 'scraping-service' },
      'crawler-service': crawler.status === 'fulfilled' ? crawler.value : { status: 'error', service: 'crawler-service' },
      'logger-service': logger.status === 'fulfilled' ? logger.value : { status: 'error', service: 'logger-service' },
    };
  },
};

// ============================================================================
// Settings API (Admin only)
// ============================================================================

export type SettingType = 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON' | 'ENCRYPTED';
export type SettingCategory = 'API' | 'PROXY' | 'CRAWLER' | 'GENERAL' | 'LOGGING' | 'EMAIL';

export interface GlobalSetting {
  id: number;
  setting_key: string;
  setting_value: string | null;
  setting_type: SettingType;
  category: SettingCategory;
  description: string | null;
  is_sensitive: boolean;
  has_value: boolean;
  source: 'database' | 'environment' | 'default' | 'none' | null;
  resolved_value: string | number | boolean | null | Record<string, unknown>;
  updated_at: string | null;
  updated_by: number | null;
}

export interface GlobalSettingsListResponse {
  settings: GlobalSetting[];
  environment: string;
  encryption_available: boolean;
}

export interface SettingsByCategoryResponse {
  api: GlobalSetting[];
  proxy: GlobalSetting[];
  crawler: GlobalSetting[];
  general: GlobalSetting[];
  logging: GlobalSetting[];
  email: GlobalSetting[];
}

export interface SettingUpdateRequest {
  setting_value: string | null;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  details?: Record<string, unknown>;
}

export interface ProviderStatus {
  name: string;
  display_name: string;
  configured: boolean;
  status: 'operational' | 'degraded' | 'major_outage' | 'unknown';
  description: string;
  status_url?: string;
  updated_at?: string;
}

export interface ProviderStatusResponse {
  providers: ProviderStatus[];
  has_issues: boolean;
  checked_at: string;
  proxy_enabled: boolean;
  proxy_configured: boolean;
  isp_configured: boolean;
  residential_configured: boolean;
}

export const settingsAPI = {
  /**
   * Get all settings (Admin only)
   */
  getAll: async (apiKey: string): Promise<GlobalSettingsListResponse> => {
    return apiCall<GlobalSettingsListResponse>(`${USER_SERVICE_URL}/api/settings`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get settings grouped by category (Admin only)
   */
  getByCategory: async (apiKey: string): Promise<SettingsByCategoryResponse> => {
    return apiCall<SettingsByCategoryResponse>(`${USER_SERVICE_URL}/api/settings/by-category`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get live status of AI providers (any authenticated user)
   */
  getProviderStatus: async (apiKey: string): Promise<ProviderStatusResponse> => {
    return apiCall<ProviderStatusResponse>(`${USER_SERVICE_URL}/api/settings/status/providers`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get a specific setting by key (Admin only)
   */
  get: async (apiKey: string, key: string): Promise<GlobalSetting> => {
    return apiCall<GlobalSetting>(`${USER_SERVICE_URL}/api/settings/${key}`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Update a setting value (Admin only)
   */
  update: async (apiKey: string, key: string, value: string | null): Promise<GlobalSetting> => {
    return apiCall<GlobalSetting>(`${USER_SERVICE_URL}/api/settings/${key}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({ setting_value: value }),
    });
  },

  /**
   * Test SMTP connection (Admin only)
   */
  testSmtp: async (apiKey: string): Promise<TestConnectionResponse> => {
    return apiCall<TestConnectionResponse>(`${USER_SERVICE_URL}/api/settings/test/smtp`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Test Anthropic API connection
   */
  testAnthropic: async (apiKey: string, testApiKey?: string): Promise<TestConnectionResponse> => {
    return apiCall<TestConnectionResponse>(`${USER_SERVICE_URL}/api/settings/test/anthropic`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({ api_key: testApiKey }),
    });
  },

  /**
   * Test OpenAI API connection
   */
  testOpenai: async (apiKey: string, testApiKey?: string): Promise<TestConnectionResponse> => {
    return apiCall<TestConnectionResponse>(`${USER_SERVICE_URL}/api/settings/test/openai`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({ api_key: testApiKey }),
    });
  },

  /**
   * Test Gemini API connection
   */
  testGemini: async (apiKey: string, testApiKey?: string): Promise<TestConnectionResponse> => {
    return apiCall<TestConnectionResponse>(`${USER_SERVICE_URL}/api/settings/test/gemini`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({ api_key: testApiKey }),
    });
  },

  /**
   * Test Perplexity API connection (for AI product search)
   */
  testPerplexity: async (apiKey: string, testApiKey?: string): Promise<TestConnectionResponse> => {
    return apiCall<TestConnectionResponse>(`${USER_SERVICE_URL}/api/settings/test/perplexity`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({ api_key: testApiKey }),
    });
  },

  /**
   * Test DeepSeek API connection
   */
  testDeepseek: async (apiKey: string, testApiKey?: string): Promise<TestConnectionResponse> => {
    return apiCall<TestConnectionResponse>(`${USER_SERVICE_URL}/api/settings/test/deepseek`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({ api_key: testApiKey }),
    });
  },

  /**
   * Test OpenRouter API connection
   */
  testOpenrouter: async (apiKey: string, testApiKey?: string): Promise<TestConnectionResponse> => {
    return apiCall<TestConnectionResponse>(`${USER_SERVICE_URL}/api/settings/test/openrouter`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({ api_key: testApiKey }),
    });
  },

  /**
   * Test Proxy connection
   */
  testProxy: async (apiKey: string): Promise<TestConnectionResponse> => {
    return apiCall<TestConnectionResponse>(`${USER_SERVICE_URL}/api/settings/test/proxy`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Test custom provider connection
   */
  testCustomProvider: async (
    apiKey: string,
    baseUrl: string,
    providerApiKey?: string,
    authHeader?: string,
    verifySsl?: boolean,
  ): Promise<TestConnectionResponse> => {
    return apiCall<TestConnectionResponse>(`${USER_SERVICE_URL}/api/ai/custom-provider/test`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        base_url: baseUrl,
        api_key: providerApiKey || '',
        auth_header: authHeader || 'Bearer',
        verify_ssl: verifySsl !== false,
      }),
    });
  },

  /**
   * Fetch models from custom provider endpoint
   */
  fetchCustomProviderModels: async (apiKey: string): Promise<{ models: Array<{ id: string; name: string }>; source: string }> => {
    return apiCall(`${USER_SERVICE_URL}/api/ai/custom-provider/models`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
    });
  },
};

// ============================================================================
// Admin User Management API
// ============================================================================

export interface AdminUser {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'user' | 'viewer';
  api_key: string;
  created_at: string;
}

export interface AdminUserListResponse {
  users: AdminUser[];
  total: number;
}

export const adminAPI = {
  /**
   * Get all users (Admin only)
   */
  getUsers: async (apiKey: string): Promise<AdminUserListResponse> => {
    return apiCall<AdminUserListResponse>(`${USER_SERVICE_URL}/api/admin/users`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Create a new user (Admin only)
   */
  createUser: async (
    apiKey: string,
    userData: { username: string; email: string; role: 'admin' | 'user' | 'viewer'; password?: string }
  ): Promise<{
    id: number;
    username: string;
    email: string;
    role: string;
    api_key: string;
    password: string;
    created_at: string;
    email_sent: boolean;
  }> => {
    return apiCall(`${USER_SERVICE_URL}/api/admin/users`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({
        username: userData.username,
        email: userData.email,
        role: userData.role.toUpperCase(),
        password: userData.password || null,
      }),
    });
  },

  /**
   * Change user role (Admin only)
   */
  changeUserRole: async (apiKey: string, userId: number, role: 'admin' | 'user' | 'viewer'): Promise<AdminUser> => {
    return apiCall<AdminUser>(`${USER_SERVICE_URL}/api/admin/users/${userId}/role`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({ role: role.toUpperCase() }),
    });
  },

  /**
   * Reset user password (Admin only)
   */
  resetUserPassword: async (apiKey: string, userId: number, newPassword: string): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${USER_SERVICE_URL}/api/admin/users/${userId}/password`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify({ new_password: newPassword }),
    });
  },

  /**
   * Delete user (Admin only)
   */
  deleteUser: async (apiKey: string, userId: number): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${USER_SERVICE_URL}/api/admin/users/${userId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey },
    });
  },
};

// ============================================================================
// Logger Service API
// ============================================================================

export interface LogEntry {
  id: number;
  container_name: string;
  log_level: string;
  message: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface LogsResponse {
  logs: LogEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface LogStats {
  total_logs: number;
  logs_today: number;
  logs_by_level: Record<string, number>;
  logs_by_container: Record<string, number>;
  retention_days: number;
}

export interface ContainerInfo {
  id: string;
  name: string;
  status: string;
  image: string;
}

export interface ContainersResponse {
  containers: ContainerInfo[];
  total: number;
}

export const loggerAPI = {
  /**
   * Get logs with pagination and filtering
   */
  getLogs: async (
    apiKey: string,
    options?: {
      container?: string;
      level?: string;
      start_time?: string;
      end_time?: string;
      page?: number;
      page_size?: number;
    }
  ): Promise<LogsResponse> => {
    const params = new URLSearchParams();
    if (options?.container) params.append('container', options.container);
    if (options?.level) params.append('level', options.level);
    if (options?.start_time) params.append('start_time', options.start_time);
    if (options?.end_time) params.append('end_time', options.end_time);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.page_size) params.append('page_size', options.page_size.toString());

    return apiCall<LogsResponse>(`${LOGGER_SERVICE_URL}/api/logs?${params}`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get log statistics
   */
  getStats: async (apiKey: string): Promise<LogStats> => {
    return apiCall<LogStats>(`${LOGGER_SERVICE_URL}/api/logs/stats`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get available containers
   */
  getContainers: async (apiKey: string): Promise<ContainersResponse> => {
    return apiCall<ContainersResponse>(`${LOGGER_SERVICE_URL}/api/logs/containers`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get WebSocket URL for real-time log streaming
   */
  getWebSocketUrl: (container?: string): string => {
    const wsUrl = LOGGER_SERVICE_URL.replace('http', 'ws');
    return `${wsUrl}/ws/logs${container ? `/${container}` : '/all'}`;
  },
};


// ============================================================================
// Dashboard API
// ============================================================================

export interface DashboardKPI {
  id: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
}

export interface RecentJob {
  id: number;
  name: string;
  project_id: number;
  project_name: string;
  task_id: number;
  task_name: string;
  status: string;
  products_extracted: number;
  products_normalized: number;
  completed_at: string | null;
}

export interface DomainCoverage {
  name: string;
  products: number;
  fill: string;
}

export interface TopCompetitor {
  name: string;
  offers: number;
  overlap: number;
  avg_dev: string;
}

export interface SellerSummary {
  total_sellers: number;
  total_offers: number;
  total_price_leaders: number;
  price_leader_share: number;
  last_crawled_at: string | null;
}

export interface DashboardResponse {
  kpis: DashboardKPI[];
  recent_jobs: RecentJob[];
  jobs_by_status: Record<string, number>;
  products_by_project: Array<{
    project_id: number;
    project_name: string;
    products_extracted: number;
    products_normalized: number;
  }>;
  domain_coverage: DomainCoverage[];
  top_competitors: TopCompetitor[];
  seller_summary?: SellerSummary | null;
}

export const dashboardAPI = {
  /**
   * Get dashboard data for current user
   */
  getDashboard: async (apiKey: string): Promise<DashboardResponse> => {
    return apiCall<DashboardResponse>(`${SCRAPING_SERVICE_URL}/api/dashboard`, {
      headers: { 'X-API-Key': apiKey },
    });
  },
};


// ============================================================================
// System Status API
// ============================================================================

export interface SystemStatusResponse {
  is_active: boolean;
  running_jobs: number;
  last_execution: string | null;
  last_job_name: string | null;
}

export const systemAPI = {
  /**
   * Get real-time system status
   */
  getStatus: async (apiKey: string): Promise<SystemStatusResponse> => {
    return apiCall<SystemStatusResponse>(`${SCRAPING_SERVICE_URL}/api/system/status`, {
      headers: { 'X-API-Key': apiKey },
    });
  },
};


// ============================================================================
// Feedback API
// ============================================================================

export type FeedbackType = 'FEATURE_REQUEST' | 'BUG_REPORT' | 'IMPROVEMENT' | 'QUESTION' | 'OTHER';
export type FeedbackPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type FeedbackStatus = 'NEW' | 'REVIEWED' | 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'DECLINED';

export interface FeedbackCreateRequest {
  title: string;
  description: string;
  feedback_type: FeedbackType;
  priority: FeedbackPriority;
  component?: string;
}

export interface FeedbackItem {
  id: number;
  title: string;
  description: string;
  feedback_type: FeedbackType;
  priority: FeedbackPriority;
  status: FeedbackStatus;
  component?: string;
  user_id: number;
  username?: string;
  admin_response?: string;
  responded_at?: string;
  created_at: string;
  updated_at: string;
}

export interface FeedbackListResponse {
  feedback: FeedbackItem[];
  total: number;
}

export const feedbackAPI = {
  /**
   * Submit feedback (feature request, bug report, etc.)
   */
  submit: async (apiKey: string, data: FeedbackCreateRequest): Promise<FeedbackItem> => {
    return apiCall<FeedbackItem>(`${USER_SERVICE_URL}/api/feedback`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get list of feedback items
   */
  list: async (
    apiKey: string,
    options?: {
      feedback_type?: FeedbackType;
      status?: FeedbackStatus;
      my_feedback_only?: boolean
    }
  ): Promise<FeedbackListResponse> => {
    const params = new URLSearchParams();
    if (options?.feedback_type) params.append('feedback_type', options.feedback_type);
    if (options?.status) params.append('status', options.status);
    if (options?.my_feedback_only) params.append('my_feedback_only', 'true');

    const queryString = params.toString();
    const url = `${USER_SERVICE_URL}/api/feedback${queryString ? `?${queryString}` : ''}`;

    return apiCall<FeedbackListResponse>(url, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Get a specific feedback item
   */
  get: async (apiKey: string, feedbackId: number): Promise<FeedbackItem> => {
    return apiCall<FeedbackItem>(`${USER_SERVICE_URL}/api/feedback/${feedbackId}`, {
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Delete a feedback item
   */
  delete: async (apiKey: string, feedbackId: number): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${USER_SERVICE_URL}/api/feedback/${feedbackId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey },
    });
  },

  /**
   * Update feedback status (admin only)
   */
  updateStatus: async (
    apiKey: string,
    feedbackId: number,
    data: { status: FeedbackStatus; admin_response?: string }
  ): Promise<FeedbackItem> => {
    return apiCall<FeedbackItem>(`${USER_SERVICE_URL}/api/feedback/${feedbackId}`, {
      method: 'PATCH',
      headers: { 'X-API-Key': apiKey },
      body: JSON.stringify(data),
    });
  },
};


// Export all APIs
export default {
  auth: authAPI,
  projects: projectsAPI,
  tasks: tasksAPI,
  jobs: jobsAPI,
  health: healthAPI,
  settings: settingsAPI,
  admin: adminAPI,
  logger: loggerAPI,
  dashboard: dashboardAPI,
  system: systemAPI,
  feedback: feedbackAPI,
};

// ============================================================================
// Selector Preview API
// ============================================================================

export interface SelectorPreviewBBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SelectorMatch {
  field: string;
  selector: string;
  match_count: number;
  first_bbox?: SelectorPreviewBBox;
  all_bboxes: SelectorPreviewBBox[];
  text_preview?: string;
  error?: string;
}

export interface SelectorPreviewResponse {
  screenshot_b64: string;
  mime_type: string;
  viewport_width: number;
  viewport_height: number;
  page_height: number;
  matches: SelectorMatch[];
  elapsed_ms: number;
  cached: boolean;
}

export const generateSelectorPreview = async (
  apiKey: string,
  jobId: number,
  url: string,
  selectors: Record<string, string>,
  refresh = false,
): Promise<SelectorPreviewResponse> => {
  const qs = refresh ? '?refresh=true' : '';
  const res = await fetch(
    `${SCRAPING_SERVICE_URL}/api/jobs/${jobId}/selector-preview${qs}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
      body: JSON.stringify({ url, selectors }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `Preview failed: ${res.status}` }));
    throw new Error(err.detail || `Preview failed: ${res.status}`);
  }
  return res.json();
};
